import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

import {
  useCreateDocumentsMutation,
  CreateDocuments_Input
} from 'generated/graphql';

export type IFormState = {
  uploadCount: number;
  documentCount: number;
  apiFileQueue: string[];
  isUploading: boolean;
  didUpload: boolean;
  apiFileIds: string[];
  folderId: string;
  projectId: string;
};

const initialState: IFormState = {
  uploadCount: 0,
  documentCount: 0,
  apiFileQueue: [],
  isUploading: false,
  didUpload: false,
  apiFileIds: [],
  folderId: '',
  projectId: ''
};

export function useFormState(projectId: string, folderId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      apiFileIds: {
        label: 'Api file ids',
        type: 'text'
      },
      folderId: {
        label: 'Folder id',
        type: 'text'
      },
      projectId: {
        label: 'Project id',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (projectId !== '' && formState.projectId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
    if (folderId !== '' && formState.folderId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'folderId',
          value: folderId
        }
      });
    }
    return () => {};
  }, [
    dispatch,
    folderId,
    formState.folderId,
    formState.projectId,
    projectId
  ]);
  const [fnCreate] = useCreateDocuments();

  // creates all documents from multiple apifileIds in single mutation. Allowing us to send notification once for multiple uploads.

  useEffect(() => {
    if (formState.uploadCount > 0 && formState.uploadCount === formState.apiFileIds.length && formState.isUploading) {
      // call to create documents
      (async () => {
        await fnCreate({
          apiFileIds: formState.apiFileIds,
          projectId: formState.projectId,
          folderId: formState.folderId !== '' ? formState.folderId : null

        });
      })();
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          apiFileQueue: [],
          uploadCount: 0,
          apiFileIds: [],
          isUploading: false,
          didUpload: false,
          documentCount: 0
        }
      });
    }
    if (formState.uploadCount > 0 && formState.uploadCount > formState.documentCount && !formState.isUploading) {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'isUploading',
          value: true
        }
      });
    }
  }, [
    dispatch,
    formState.apiFileQueue.length,
    formState.isUploading,
    formState.didUpload,
    formState.uploadCount,
    formState.documentCount,
    formState.apiFileIds,
    formState.projectId,
    formState.folderId,
    fnCreate,
    formState
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useCreateDocuments() {
  const [
    fnMutate,
    tuple
  ] = useCreateDocumentsMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: CreateDocuments_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
